import React, { useEffect, useRef, useState } from "react";
import geojson from "./usa-topo.json";
import { feature } from "topojson-client";
import * as d3 from "d3";
import "../scss/map.scss";
import * as tileDictionary from "../Util/tileDictionary";
import * as analytics from "../Util/Analytics/MapAnalytics";

let state = null;
let smallStateBoxItems = [];
let smallStateTextItems = [];
let allStates = []; // Track all state elements for keyboard navigation

const Map = ({
  onVizTypeChange,
  question,
  subQuestion,
  subQuestionCSVData,
  vizType,
  showSampleSizeOnMap = false,
}) => {
  const svgContainer = useRef();
  // Track latest Show Sample Size toggle; D3 handlers otherwise capture stale values
  const showSampleSizeRef = useRef(showSampleSizeOnMap);
  const [currentFocusIndex, setCurrentFocusIndex] = useState(0);

  useEffect(() => {
    document.title =
      "School Health Profiles Explorer | Data Visualization: Map";
  }, []);

  useEffect(() => {
    const map = document.getElementById("subquestion-map-container");
    // Only auto-scroll when there is actual map data for the
    // current subquestion/year; avoids flashing when rapidly
    // clicking subquestions that have no data.
    const hasMapRows =
      subQuestionCSVData &&
      ((Array.isArray(subQuestionCSVData) &&
        subQuestionCSVData.length > 0) ||
        (!Array.isArray(subQuestionCSVData) &&
          subQuestionCSVData.tableData &&
          subQuestionCSVData.tableData.length > 0));

    if (map && hasMapRows) {
      try {
        const rect = map.getBoundingClientRect();
        const viewportHeight =
          typeof window !== "undefined"
            ? window.innerHeight || document.documentElement.clientHeight
            : 0;

        // Only auto-scroll when the map container is meaningfully
        // outside the viewport to reduce jumping when switching
        // subquestions while already near the map.
        const margin = 40; // px tolerance
        const aboveViewport = rect.top < margin;
        const belowViewport = rect.bottom > viewportHeight - margin;

        if (aboveViewport || belowViewport) {
          scrollToMap(map);
        }
      } catch (e) {
        scrollToMap(map);
      }
    }
  }, [vizType, subQuestionCSVData]);

  const scrollToMap = (element) => {
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "start",
      });
    }
  };

  // removing tooltip when compenet unmounts
  useEffect(() => () => removeTooltip());

  useEffect(() => {
    removeTooltip();
    if (subQuestionCSVData) {
      ShowMap(geojson, subQuestionCSVData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subQuestion, subQuestionCSVData]);

  // Close any open tooltip when toggle changes so next open reflects new state
  useEffect(() => {
    removeTooltip();
  }, [showSampleSizeOnMap]);

  // Keep ref in sync with prop for use inside D3 event handlers
  useEffect(() => {
    showSampleSizeRef.current = showSampleSizeOnMap;
  }, [showSampleSizeOnMap]);

  // Keyboard navigation handler
  const handleMapKeyDown = (event) => {
    if (!allStates || allStates.length === 0) return;
    
    let newIndex = currentFocusIndex;
    const statesPerRow = 10; // Approximate for arrow key navigation
    
    switch(event.key) {
      case 'ArrowRight':
        event.preventDefault();
        newIndex = (currentFocusIndex + 1) % allStates.length;
        break;
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = (currentFocusIndex - 1 + allStates.length) % allStates.length;
        break;
      case 'ArrowDown':
        event.preventDefault();
        newIndex = (currentFocusIndex + statesPerRow) % allStates.length;
        break;
      case 'ArrowUp':
        event.preventDefault();
        newIndex = (currentFocusIndex - statesPerRow + allStates.length) % allStates.length;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (allStates[currentFocusIndex]) {
          allStates[currentFocusIndex].element.node().dispatchEvent(new Event('click', { bubbles: true }));
        }
        return;
      case 'Tab':
        // Allow natural tab behavior
        return;
      default:
        return;
    }
    
    setCurrentFocusIndex(newIndex);
    if (allStates[newIndex]) {
      // Focus the state element
      allStates[newIndex].element.node().focus();
      // Add visual highlight
      d3.selectAll('.state-focused').classed('state-focused', false);
      allStates[newIndex].element.classed('state-focused', true);
    }
  };

  let zoom = d3.zoom().scaleExtent([1, 8]).on("zoom", zoomed);

  let ResetZoom = () => {
    zoom.transform(state, d3.zoomIdentity);
  };
  function ZoomIn() {
    zoom.scaleBy(state.transition().duration(300), 1.5);
  }
  function ZoomOut() {
    zoom.scaleBy(state.transition().duration(300), 0.9);
  }
  function zoomed(event) {
    const { transform } = event;
    state.attr("transform", transform);
    state.attr("stroke-width", 2 / transform.k);

    smallStateBoxItems.forEach((item, index) => {
      item.obj.attr("transform", transform);
      item.obj.attr("stroke-width", 2 / transform.k);
    });
    smallStateTextItems.forEach((item, index) => {
      item.obj.attr("transform", transform);
      item.obj.attr("stroke-width", 2 / transform.k);
    });
  }

  function getColor(currentLocation, data, tileDictionary) {
    let fillColor, textColor;
    const tableData = data && Array.isArray(data.tableData) ? data.tableData : [];
    const legendData = data && Array.isArray(data.legendData) ? data.legendData : [];
    const stateData = tableData.filter(
      (i) => i.LocationTxt === currentLocation
    );
    if (!stateData.length || legendData.length === 0) {
      return [false, false];
    }
    const percentage =
      stateData.length > 0 && stateData[0].NPercent !== null
        ? parseFloat(stateData[0].NPercent)
        : 0;
    const gradientIndex =
      percentage > 0
        ? legendData.findIndex(
            (i) =>
              percentage >= i.LowValue.toFixed(1) &&
              percentage <= i.HighValue.toFixed(1)
          ) + 1
        : 0;
    const topicColors =
      tileDictionary &&
      tileDictionary.topicDictionary &&
      tileDictionary.topicDictionary[stateData[0].TopicId] &&
      Array.isArray(tileDictionary.topicDictionary[stateData[0].TopicId].colors)
        ? tileDictionary.topicDictionary[stateData[0].TopicId].colors
        : null;
    if (!topicColors || topicColors.length === 0) {
      return [false, false];
    }

    fillColor =
      stateData && stateData.length > 0 && gradientIndex >= 0
        ? topicColors
            .slice()
            .reverse()[gradientIndex].fillColor
        : false;
    textColor =
      stateData && stateData.length > 0 && gradientIndex >= 0
        ? topicColors
            .slice()
            .reverse()[gradientIndex].textColor
        : false;
    return [fillColor, textColor];
  }

  function ShowMap(mapData, data) {
    // Reset allStates array
    allStates = [];
    
    let svgScaleFactor = null;
    const clientWidth =
      svgContainer.current && svgContainer.current.clientWidth;

    switch (true) {
      case clientWidth < 550:
        svgScaleFactor = 1.8;
        break;
      case clientWidth >= 550 && clientWidth < 675:
        svgScaleFactor = 1.5;
        break;
      case clientWidth >= 675 && clientWidth < 850:
        svgScaleFactor = 1.4;
        break;
      case clientWidth >= 850 && clientWidth < 1030:
        svgScaleFactor = 1.2;
        break;
      case clientWidth >= 1030 && clientWidth < 1210:
        svgScaleFactor = 1.1;
        break;
      default:
        svgScaleFactor = 1;
    }

    const leftPadding = 40;
    const topPadding = 23;

    let width =
      svgContainer.current &&
      svgContainer.current.clientWidth * svgScaleFactor - 2 * leftPadding;
    let height = width * 0.6 - 2 * topPadding;

    d3.select("#subquestion-map-container svg").remove();
    const tableData = data && Array.isArray(data) ? data : data.tableData;
    const statesWithData = tableData.map((i) => i.LocationTxt);
    const svg = d3
      .select("#subquestion-map-container")
      .append("svg")
      .attr("width", width)
      .attr("height", height + 85)
      .attr("style", "border: 1px solid #ddddd")
      .attr("viewBox", `-60, 0, ${width * svgScaleFactor}, ${height}`)
      .attr("preserveAspectRatio", "xMaxYMin meet")
      .attr("style", "width: 100%; height: auto; height: intrinsic;");

    // Define diagonal hatch pattern for local data overlay
    const defs = svg.append("defs");
    const hatch = defs
      .append("pattern")
      .attr("id", "diagonalHatch")
      .attr("patternUnits", "userSpaceOnUse")
      .attr("width", 8)
      .attr("height", 8)
      .attr("patternTransform", "rotate(45)");
    hatch
      .append("rect")
      .attr("width", 8)
      .attr("height", 8)
      .attr("fill", "transparent");
    hatch
      .append("line")
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", 0)
      .attr("y2", 8)
      .attr("stroke", "#222")
      .attr("stroke-width", 8)
      .attr("opacity", 0.6);

    /***********************************************************************************
     * Display state polygon for object is data and add
     * diamond at midpoint of each state with local data
     ***********************************************************************************/
    const localDataItems = data.tableData.filter(
      (i) => i.LocationType === "Local"
    );
    const localDataStates = Array.from(
      new Set(localDataItems.map((i) => i.ParentLocationDesc))
    );
    state = svg
      .append("g")
      .attr("id", "map")
      .attr("transform", "translate(0, 0)");
    
    const stateGroups = state
      .attr("class", "states")
      .selectAll("g")
      .data(feature(mapData, mapData.objects.states).features)
      .join("g")
      .attr("id", (d) => d.properties.name.replace(/\s/g, "-"))
      .attr("class", (d) =>
        localDataStates.includes(d.properties.name)
          ? "state-group local-data"
          : "state-group"
      );
    
    const statePaths = stateGroups
      .append("path")
      .attr("d", d3.geoPath())
      .attr("stroke-opacity", "1")
      .attr("stroke", "white")
      .attr("fill", "#ccc") // default fill (no data)
      .attr("tabindex", "0")
      .attr("role", "button")
      .attr("aria-label", (d) => {
        const stateData = data.tableData.filter(
          (i) => i.LocationTxt === d.properties.name
        );
        const percentage = stateData.length > 0 && stateData[0].NPercent !== null
          ? parseFloat(stateData[0].NPercent)
          : null;
        return percentage !== null 
          ? `${d.properties.name}, ${percentage}%`
          : `${d.properties.name}, No data`;
      })
      .style("cursor", "pointer")
      .style("stroke-width", function (d) {
        return 1;
      })
      .style("fill", function (d) {
        if (statesWithData.includes(d.properties.name)) {
          return getColor(d.properties.name, data, tileDictionary)[0];
        }
      })
      .on("click", function (event, d) {
        removeTooltip();
        const textColor = getColor(d.properties.name, data, tileDictionary)[1];
        const fillColor = getColor(d.properties.name, data, tileDictionary)[0];
        event.currentTarget.classList.add("hovered");
        showTooltip(event, d, fillColor, textColor);
      })
      .on("keydown", function(event, d) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          removeTooltip();
          const textColor = getColor(d.properties.name, data, tileDictionary)[1];
          const fillColor = getColor(d.properties.name, data, tileDictionary)[0];
          event.currentTarget.classList.add("hovered");
          showTooltip(event, d, fillColor, textColor);
        }
      })
      .on("focus", function(event, d) {
        d3.select(this).classed("state-focused", true);
      })
      .on("blur", function(event, d) {
        d3.select(this).classed("state-focused", false);
      });
    
    // Store state paths for keyboard navigation
    statePaths.each(function(d, i) {
      allStates.push({
        name: d.properties.name,
        element: d3.select(this),
        index: i
      });
    });

    // Overlay hatch on states with local data available
    state
      .selectAll("g.state-group.local-data")
      .append("path")
      .attr("d", d3.geoPath())
      .attr("fill", "url(#diagonalHatch)")
      .attr("stroke", "none")
      .style("pointer-events", "none")
      .attr("opacity", 0.6);

    /***********************************************************************************
     * Generating local data symbols for larger states
     ************************************************************************************/
    // Removed adjustSymbolSpacing; not needed with hatch overlay approach

    const smallStateNames = [
      "Connecticut",
      "Delaware",
      "District of Columbia",
      "Massachusetts",
      "Rhode Island",
      "Hawaii",
      "Maryland",
    ];

    // Removed old generateLocalDataIcons; hatch overlay now indicates local data

  // generateLocalDataIcons(".state-group.local-data"); // replaced by hatch overlay

    // National data will now be rendered inside #small-states-group as a special first entry

    /*************************************
     * Adding legend
     *************************************/
    d3.selectAll("#legend-group").remove(); // Remove legend for previous subquestion
    const legendWidth = 140;

    const legendGroup = d3.select("#subquestion-map-container").append("div");

    legendGroup
      .attr("id", "legend-group")
      .append("div")
      .attr("id", "legend-label")
      .text("Percent (%)")
      .attr("font-weight", "bold")
      .attr("font-size", "17px")
      .attr("fill", "black")
      .attr("x", legendWidth / 2 - 60)
      .attr("y", 32);

    const legendData = data && Array.isArray(data.legendData) ? data.legendData : [];
    const legendDataLabels = legendData
      .slice()
      .reverse()
      .map((i) => i.FullText);
    legendDataLabels.push("No data");
    legendDataLabels.push("Local data");

    const valueContainer = d3.select("#legend-group").append("div");
    valueContainer.attr("id", "value-container");
    const valueGroups = valueContainer.append("div");
    const valueColors =
      tileDictionary &&
      tileDictionary.topicDictionary &&
      tileDictionary.topicDictionary[question.TopicId] &&
      Array.isArray(tileDictionary.topicDictionary[question.TopicId].colors)
        ? tileDictionary.topicDictionary[question.TopicId].colors
        : [];
    valueGroups.attr("id", "value-groups");

    valueGroups
      .selectAll("div")
      .data(legendDataLabels)
      .join("div")
      .attr("class", "value-group")
      .html(
        (d, index) =>
          `<div class='small-circle' style="background-color:${valueColors[index] && valueColors[index].fillColor};"></div><div class="value-label">${d}</div>`
      );

    // Legend: use striped hatch to indicate local data availability (thicker and slightly darker)
    // Enlarge to 25x25 square for clearer example
    d3.select("#value-groups .value-group:last-child .small-circle")
      .style(
        "background-image",
        "repeating-linear-gradient(-45deg, rgba(0,0,0,0.6) 0px, rgba(0,0,0,0.6) 6px, transparent 6px, transparent 12px)"
      )
      .style("background-color", "#e9ecef")
      .style("width", "25px")
      .style("height", "25px")
      .style("border-radius", "0");

    const smallTerritory_x = 0;
    const smallTerritory_y = 650;
    const smallTerritory_width = 60;
    const smallTerritory_height = 35;

    const smallStateItemBox = svg
      .select("#map")
      .append("g")
      .attr(
        "transform",
        `translate(${
          width * svgScaleFactor - (legendWidth + 3) - 145
        }, ${-370})`
      )
      .attr("id", "small-states-group");


      // --- Add US (National) as first, round entry in small-states-group ---
      const nationalEntries = tableData.filter((i) => i.LocationType === "National");
      if (nationalEntries.length > 0) {
        const usDatum = nationalEntries[0];
        const usGroup = smallStateItemBox
          .append("g")
          .attr("id", "US-circle")
          .attr("class", "small-states us-small-state")
          .attr("tabindex", "0")
          .attr("role", "button")
          .attr("aria-label", () => {
            const percentage = usDatum.NPercent !== null ? parseFloat(usDatum.NPercent) : null;
            return percentage !== null 
              ? `United States, ${percentage}%`
              : `United States, No data`;
          })
          .on("click", function (event) {
            const textColor = getColor(usDatum.LocationTxt, data, tileDictionary)[1];
            const fillColor = getColor(usDatum.LocationTxt, data, tileDictionary)[0];
            const item = { properties: { name: usDatum.LocationTxt } };
            removeTooltip();
            showTooltip(event, item, fillColor, textColor);
          })
          .on("keydown", function(event) {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              const textColor = getColor(usDatum.LocationTxt, data, tileDictionary)[1];
              const fillColor = getColor(usDatum.LocationTxt, data, tileDictionary)[0];
              const item = { properties: { name: usDatum.LocationTxt } };
              removeTooltip();
              showTooltip(event, item, fillColor, textColor);
            }
          })
          .on("focus", function() {
            d3.select(this).classed("state-focused", true);
          })
          .on("blur", function() {
            d3.select(this).classed("state-focused", false);
          });

        // Place US circle slightly above the first small state with extra spacing
        const usCenterX = smallTerritory_x + smallTerritory_width / 2;
        const usCenterY = smallTerritory_y - 80; // space above the first row
        const usRadius = 30;

        // Circle styled using the same topic-driven color logic as other
        // small-state rectangles so the US button matches the legend.
        const usColors = getColor(usDatum.LocationTxt, data, tileDictionary);
        const usFillColor = usColors && usColors[0] ? usColors[0] : "#005b8f";
        const usTextColor = usColors && usColors[1] ? usColors[1] : "#ffffff";

        usGroup
          .append("circle")
          .attr("cx", usCenterX)
          .attr("cy", usCenterY)
          .attr("r", usRadius)
          .style("fill", usFillColor)
          .style("stroke", "#7d8fa3")
          .style("stroke-width", 1.5)
          .style("cursor", "pointer");

        usGroup
          .append("text")
          .attr("x", usCenterX)
          .attr("y", usCenterY + 2)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .text("US")
          .style("fill", usTextColor)
          .attr(
            "font-family",
            ' "Open Sans",apple-system,blinkmacsystemfont,"Segoe UI","Helvetica Neue",arial,sans-serif'
          )
          .style("font-size", "15px")
          .style("font-weight", "bold")
          .style("pointer-events", "none");
        
        // Add to allStates for keyboard navigation
        allStates.push({
          name: "United States",
          element: usGroup,
          index: allStates.length
        });
      }
     
    const smallStates = mapData.objects.states.geometries.filter((d) => {
      return smallStateNames.includes(d.properties.name);
    });
    smallStates.sort((a, b) =>
      a.properties.name > b.properties.name ? 1 : -1
    );
    smallStates.forEach((smallState, index) => {
      const stateObject = tableData.filter(
        (i) => i.LocationTxt === smallState.properties.name
      )[0] || null;
      const smallStateGroup = smallStateItemBox
        .append("g")
        .attr("id", smallState.properties.name.replace(/\s/g, "-"))
        .attr("class", "small-states")
        .attr("tabindex", "0")
        .attr("role", "button")
        .attr("aria-label", () => {
          const percentage = stateObject && stateObject.NPercent !== null 
            ? parseFloat(stateObject.NPercent) 
            : null;
          return percentage !== null 
            ? `${smallState.properties.name}, ${percentage}%`
            : `${smallState.properties.name}, No data`;
        })
        .on("click", function (event, d) {
          const textColor = getColor(
            smallState.properties.name,
            data,
            tileDictionary
          )[1];
          const fillColor = getColor(
            smallState.properties.name,
            data,
            tileDictionary
          )[0];
          if (stateObject) {
            stateObject.properties = { name: stateObject.LocationTxt };
          }
          removeTooltip();
          showTooltip(event, smallState, fillColor, textColor);
        })
        .on("keydown", function(event, d) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const textColor = getColor(
              smallState.properties.name,
              data,
              tileDictionary
            )[1];
            const fillColor = getColor(
              smallState.properties.name,
              data,
              tileDictionary
            )[0];
            if (stateObject) {
              stateObject.properties = { name: stateObject.LocationTxt };
            }
            removeTooltip();
            showTooltip(event, smallState, fillColor, textColor);
          }
        })
        .on("focus", function() {
          d3.select(this).classed("state-focused", true);
        })
        .on("blur", function() {
          d3.select(this).classed("state-focused", false);
        });
      
      smallStateGroup
        .append("rect")
        .attr("x", smallTerritory_x)
        .attr("y", smallTerritory_y + index * 47)
        .attr("rx", 5)
        .attr("ry", 5)
        .style("stroke", "grey")
        .style("fill", () => {
          return getColor(smallState.properties.name, data, tileDictionary)[0];
        })
        .style("stroke-width", function (d) {
          return 1.5;
        })
        .attr("width", smallTerritory_width)
        .attr("height", smallTerritory_height)
        .style("stroke", "grey")
        .style("cursor", "pointer");
      
      // Add to allStates for keyboard navigation
      allStates.push({
        name: smallState.properties.name,
        element: smallStateGroup,
        index: allStates.length
      });

      // Overlay hatch on small-state rectangles if local data available
      if (localDataStates.includes(smallState.properties.name)) {
        smallStateGroup
          .append("rect")
          .attr("x", smallTerritory_x)
          .attr("y", smallTerritory_y + index * 47)
          .attr("rx", 5)
          .attr("ry", 5)
          .attr("width", smallTerritory_width)
          .attr("height", smallTerritory_height)
          .attr("fill", "url(#diagonalHatch)")
          .attr("stroke", "none")
          .style("pointer-events", "none")
          .attr("opacity", 0.6);
      }

      /*****************************************
       * Generating text for smaller states
       ******************************************/
      // Center the abbreviation text within each small-state rectangle
      smallStateGroup
        .append("text")
        .attr("class", "territory-text")
        .attr("x", smallTerritory_x + smallTerritory_width / 2)
        .attr("y", smallTerritory_y + index * 47 + smallTerritory_height / 2)
        .text(() => (stateObject && stateObject.LocationAbbr) ? stateObject.LocationAbbr : "")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .style("fill", () => {
          // Force white text when hatch overlay (local data) is present for better contrast
          return localDataStates.includes(smallState.properties.name)
            ? "#ffffff"
            : getColor(smallState.properties.name, data, tileDictionary)[1];
        })
        .attr(
          "font-family",
          ' "Open Sans",apple-system,blinkmacsystemfont,"Segoe UI","Helvetica Neue",arial,sans-serif'
        )
        .style("font-size", "15px")
        .style("font-weight", "bold")
        .style("pointer-events", "none");

      // Removed diamond local-data icons for small states; hatch overlay is used instead
    });

    svg.call(zoom);
  }

  function showTooltip(event, i, fillColor, textColor) {
    const filteredTableData =
      subQuestionCSVData && Array.isArray(subQuestionCSVData)
        ? subQuestionCSVData.filter(function (DataItem) {
            return (
              DataItem.LocationTxt === i.properties.name ||
              DataItem.ParentLocationDesc === i.properties.name
            );
          })
        : subQuestionCSVData.tableData.filter(function (DataItem) {
            return (
              DataItem.LocationTxt === i.properties.name ||
              DataItem.ParentLocationDesc === i.properties.name
            );
          });

    /**********************************************************
     * Prevent error in 'no data' usecase by adding
     * at leat one item to filteredTableData where
     * there is no data.
     **********************************************************/
    filteredTableData.length === 0 && filteredTableData.push(i);
    /**-------------------------------------------------------- */

    const showMultiTooltip = filteredTableData.length > 1;

    const topicColor =
      tileDictionary.topicDictionary[question.TopicId].colors[0].fillColor;

    const tooltipContainerElement = showMultiTooltip
      ? "#subquestion-map-container"
      : "body";
    const tooltip = d3
      .select(tooltipContainerElement)
      .append("div")
      .attr("id", () => {
        const id = showMultiTooltip ? "multi-tooltip" : "tooltip";
        return id;
      })
      .attr("class", "tooltip")
      .style("position", () => "absolute")
      .style("opacity", "1")
      .style(
        "top",
        () => `${showMultiTooltip ? "17%" : event.pageY - 60 + "px"}`
      )
      .style("left", () => {
        const svgWidth = svgContainer.current.clientWidth;
        const value =
          event.pageX > svgWidth - 250
            ? `${
                showMultiTooltip
                  ? "30%"
                  : event.pageX -
                    parseInt(d3.select(".tooltip").node().clientWidth) * 1.1 +
                    "px"
              }`
            : `${showMultiTooltip ? "30%" : event.pageX + 11 + "px"}`;
        return value;
      });

    tooltip
      .append("div")
      .attr("class", "tooltip-close-container")
      .style("display", () => (showMultiTooltip ? "flex" : "none"))
      .html(() => '<span class="cdc-icon-close close-tooltip" />');

  const tooltip_content = tooltip.append("div").attr("id", "tooltip-content");

    tooltip_content
      .selectAll("section")
      .data(filteredTableData)
      .join("section")
      .attr("class", (item) => item.LocationType)
      .attr("id", (item, index) => `location_${index}`)
      .html((item) => {
        const tooltipColor = getColor(
          item.LocationTxt,
          subQuestionCSVData,
          tileDictionary
        );
        const dataString =
          Object.keys(item).includes("LocationTxt") && item.NPercent !== null
            ? `
              <div class="tooltip-header"
              style="background-color:
                ${tooltipColor[0]};
                display:flex;
                justify-content:space-between;">
                <span 
                  style="font-size:1rem;
                  font-weight:bold;
                  color:${tooltipColor[1]};"
                >
                ${item.LocationTxt}</span>
                ${
                  showMultiTooltip
                    ? ""
                    : `<span class="cdc-icon-close close-tooltip" style="color:${tooltipColor[1]};" />`
                }
              </div>
              <div class="tooltip-data">
                ${(showSampleSizeRef.current && item.SampleSize != null) ? `<div>N: ${item.SampleSize}</div>` : ``}
                <div><b>%: ${item.NPercent}</b></div>
                <div>CI: ${item.ConfidenceInterval}</div>
                <div
                  style="font-size:.65rem;cursor:pointer"
                  id="tooltip-link"
                  data=${item.LocationAbbr}
                >
                  View ${item.LocationTxt} Survey Results
                </div>
              </div>`
            : `<div class="tooltip-data" style="color:black;display:flex;"><span > <b>${i.properties.name}</b>: No data </span><span class="cdc-icon-close close-tooltip" style="padding-left: 17px;font-weight:bold;"/></div>`;
        return dataString;
      });

    const displayStateHeader = filteredTableData
      .map((datum) => datum.LocationType)
      .includes("State");

    const displayLocalHeader = filteredTableData
      .map((datum) => datum.LocationType)
      .includes("Local");

    d3.select("#tooltip-content")
      .style("padding", () =>
        !showMultiTooltip && displayLocalHeader ? "8px" : ""
      )
      .insert("span", ".State")
      .attr("class", "location-type-header")
      .attr("id", "state-header")
      .style("display", () =>
        showMultiTooltip && displayStateHeader ? "block" : "none"
      )
      .style("color", topicColor)
      .html("State Surveys");

    d3.select("#tooltip-content")
      .insert("span", ".Local")
      .attr("class", "location-type-header")
      .attr("id", "local-header")
      .style("display", () => (displayLocalHeader ? "block" : "none"))
      .style("color", topicColor)
      .html("Local Surveys");

    tooltip.selectAll(".close-tooltip").on("click", removeTooltip);
    tooltip.selectAll("#tooltip-link").on("click", (e) => {
      const locationId = e.target.getAttribute("data");
      analytics.SelectMapLink();
      analytics.SelectMapLocation({ locationId });
      removeTooltip();
      onVizTypeChange({
        vizType: "table",
        questionId: question.QuestionId,
        subQuestionId: subQuestion ? subQuestion.SubQuestionId : null,
        locationAbbr: locationId, // returns abbreviation for state and local objects
      });
    });
  }

  function removeTooltip() {
    d3.select(".tooltip").style("opacity", "0");
    d3.select(".tooltip").remove();
  }

  return (
    subQuestionCSVData && (
      <>
        <div
          className="text-center"
          ref={svgContainer}
          id="subquestion-map-container"
          tabIndex="-1"
          role="region"
          aria-label="Map visualization. Use arrow keys to navigate between states and Enter to select."
          onKeyDown={handleMapKeyDown}
        >
          <div id="map-controls">
            <div className="map-control" onClick={ZoomIn} id="zoom-in" tabIndex="0" role="button" aria-label="Zoom in" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ZoomIn(); } }}>
              <span className="cdc-icon-plus"></span>
            </div>
            <div className="map-control" onClick={ZoomOut} id="zoom-out" tabIndex="0" role="button" aria-label="Zoom out" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ZoomOut(); } }}>
              <span className="cdc-icon-minus"></span>
            </div>

            <div className="map-control" onClick={ResetZoom} id="zoom-reset" tabIndex="0" role="button" aria-label="Reset zoom" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ResetZoom(); } }}>
              <span className="cdc-icon-replay"></span>
            </div>
          </div>
        </div>
      </>
    )
  );
};

export default Map;
